import { z } from 'zod';

/**
 * Perfil de la empresa (ADR-0011). La empresa completa desde el portal los datos
 * que el cliente necesita para facturarle (razón social, CUIT, condición frente
 * al IVA, domicilio fiscal, email de facturación, teléfono) y su logo. El logo
 * viaja como base64 (igual que las firmas) y se embebe en el PDF del comprobante.
 */

export type CondicionIVA = 'RI' | 'MONOTRIBUTO' | 'EXENTO' | 'CF';

export const CONDICIONES_IVA = ['RI', 'MONOTRIBUTO', 'EXENTO', 'CF'] as const;

export const ETIQUETA_CONDICION_IVA: Record<CondicionIVA, string> = {
  RI: 'Responsable Inscripto',
  MONOTRIBUTO: 'Monotributo',
  EXENTO: 'Exento',
  CF: 'Consumidor Final',
};

/** Tamaño máximo del logo (binario decodificado). Se revalida en el servidor. */
export const LOGO_MAX_BYTES = 512 * 1024;

const vacioANull = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? null : v);
const textoOpcional = (max: number) =>
  z.preprocess(vacioANull, z.string().trim().max(max).nullable().optional());

export const esquemaActualizarPerfilEmpresa = z.object({
  razonSocial: textoOpcional(160),
  cuit: z.preprocess(
    vacioANull,
    z.string().regex(/^\d{2}-\d{8}-\d$/, 'CUIT inválido (formato XX-XXXXXXXX-X)').nullable().optional(),
  ),
  condicionIva: z.preprocess(vacioANull, z.enum(CONDICIONES_IVA).nullable().optional()),
  domicilioFiscal: textoOpcional(240),
  emailFacturacion: z.preprocess(
    vacioANull,
    z.string().email('Email de facturación inválido').max(160).nullable().optional(),
  ),
  telefono: textoOpcional(40),
  // El base64 ronda 4/3 del binario; el límite fino (bytes) se valida en el server.
  logoBase64: z.preprocess(vacioANull, z.string().max(1_000_000).nullable().optional()),
  logoMime: z.preprocess(vacioANull, z.enum(['image/png', 'image/jpeg']).nullable().optional()),
});

export type ActualizarPerfilEmpresaInput = z.infer<typeof esquemaActualizarPerfilEmpresa>;

export interface PerfilEmpresaDTO {
  id: string;
  nombre: string;
  razonSocial: string | null;
  cuit: string | null;
  condicionIva: CondicionIVA | null;
  domicilioFiscal: string | null;
  emailFacturacion: string | null;
  telefono: string | null;
  logoBase64: string | null;
  logoMime: string | null;
  actualizadoEn: string | null;
}
